import type {
  IncidentErnst,
  IncidentGegevenssoort,
  IncidentGroep,
  IncidentSignalRule,
  IncidentStatus,
} from '@kennisbank/shared';

/**
 * Nederlandse labels bij de Engelse enums uit `shared`. Code is Engels, UI is
 * Nederlands (plan §3). Eén plek, zodat wachtrij, detail en triageformulier
 * dezelfde woorden gebruiken als de melder in het formulier zag.
 */

export const STATUS_LABEL: Record<IncidentStatus, string> = {
  nieuw: 'Nieuw',
  in_behandeling: 'In behandeling',
  afgerond: 'Afgerond',
};

export const STATUS_CHIP: Record<IncidentStatus, string> = {
  nieuw: 'bg-petrol-50 text-petrol-700',
  in_behandeling: 'bg-mosterdgeel-10 text-ring1-fg',
  afgerond: 'bg-ink-50 text-ink-900/60',
};

export const ERNST_LABEL: Record<IncidentErnst, string> = {
  laag: 'Laag',
  midden: 'Midden',
  hoog: 'Hoog',
};

/** Woordelijk gelijk aan de opties in het melderformulier (IncidentMelden). */
export const GROEP_LABEL: Record<IncidentGroep, string> = {
  patient: 'Patiënten of cliënten',
  medewerker: 'Medewerkers',
  anders: 'Anders',
  geen_of_onbekend: 'Geen of nog niet bekend',
};

export const GEGEVENSSOORT_LABEL: Record<IncidentGegevenssoort, string> = {
  gezondheid: 'Gegevens over gezondheid',
  politiek_levensbeschouwelijk:
    'Gegevens over politieke opvattingen, godsdienst of levensovertuiging',
  seksueel_gedrag_gerichtheid: 'Gegevens over seksueel gedrag of seksuele gerichtheid',
  etnische_afkomst: 'Gegevens over ras of etnische afkomst',
  administratief:
    'Administratieve gegevens, zoals BSN, paspoort- of identiteitsnummer, rekeningnummer of loon',
  geen_of_onbekend: 'Geen of nog niet bekend',
};

/**
 * De elf vragen zoals de melder ze zag. De beheerder leest hier hetzelfde
 * formulier terug, in dezelfde volgorde en met dezelfde woorden, zodat de
 * opgave van de melder niet gaandeweg van betekenis verandert.
 */
export const VRAAG_TITEL: Record<number, string> = {
  1: 'Wat is je naam?',
  2: 'Hoe is het (mogelijke) datalek ontdekt?',
  3: 'Wat is er gebeurd?',
  4: 'Op welke datum vond het (mogelijke) datalek plaats?',
  5: 'Op welke datum is het ontdekt?',
  6: 'Van welke groepen personen zijn persoonsgegevens gelekt?',
  7: 'Welke bijzondere persoonsgegevens zijn gelekt?',
  8: 'Zijn er andere organisaties bij betrokken?',
  9: 'Zo ja, welke organisaties?',
  10: 'Welke actie heb je zelf al ondernomen?',
  11: 'Welke maatregelen zijn volgens jou nodig om dit in de toekomst te voorkomen?',
};

export const GROEP_VOLGORDE: IncidentGroep[] = [
  'patient',
  'medewerker',
  'anders',
  'geen_of_onbekend',
];

export const GEGEVENSSOORT_VOLGORDE: IncidentGegevenssoort[] = [
  'gezondheid',
  'politiek_levensbeschouwelijk',
  'seksueel_gedrag_gerichtheid',
  'etnische_afkomst',
  'administratief',
  'geen_of_onbekend',
];

/**
 * Signaalregels in gewone taal. De beheerder hoeft niet te weten dat er een
 * enum `herhaalde_weigering` bestaat; zij moet zien wat er gebeurd is.
 */
export const SIGNAAL_TITEL: Record<IncidentSignalRule, string> = {
  herhaalde_weigering: 'Iemand kreeg herhaaldelijk toegang geweigerd',
  geweigerde_inlogpogingen: 'Herhaalde geweigerde inlogpogingen',
  bulk_download: 'Veel documenten in korte tijd gedownload',
  download_in_de_nacht: 'Document gedownload in de nacht',
};

export const SIGNAAL_UITLEG: Record<IncidentSignalRule, string> = {
  herhaalde_weigering:
    'Een gebruiker probeerde meermaals bij iets te komen waar de rol geen toegang toe geeft. Vaak een verkeerde link of een verlopen rol, soms iemand die grenzen aftast.',
  geweigerde_inlogpogingen:
    'Aanmeldingen zijn geweigerd omdat het account is ingetrokken of geen rol heeft. Iemand probeert binnen te komen met een account dat niet meer geldig is.',
  bulk_download:
    'Eén gebruiker downloadde in korte tijd ongebruikelijk veel documenten. Dat kan werkvoorbereiding zijn, of het meenemen van een kennisbank.',
  download_in_de_nacht:
    'Er is tussen middernacht en 6 uur een document gedownload. POH-GGZ werken vaak in de avond, dus dit venster is bewust smal gehouden.',
};
